import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  DollarSign, Image, Save, Loader2, ArrowLeft, Trash2, Plus, Star, Camera
} from "lucide-react";

export default function ProfessionalProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    business_name: "",
    bio: "",
    service_area: "",
    hourly_rate: "",
    price_range_min: "",
    price_range_max: "",
    portfolio_images: []
  });

  useEffect(() => {
    if (user) {
      setFormData({
        business_name: user.business_name || "",
        bio: user.bio || "",
        service_area: user.service_area || "",
        hourly_rate: user.hourly_rate || "",
        price_range_min: user.price_range_min || "",
        price_range_max: user.price_range_max || "",
        portfolio_images: user.portfolio_images || []
      });
    }
  }, [user]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 4 - formData.portfolio_images.length;
    
    if (files.length > remaining) {
      toast.error(`You can only add ${remaining} more image(s). Maximum is 4.`);
      return;
    }

    files.slice(0, remaining).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max size is 5MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          portfolio_images: [...prev.portfolio_images, e.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      portfolio_images: prev.portfolio_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        business_name: formData.business_name,
        bio: formData.bio,
        service_area: formData.service_area,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        price_range_min: formData.price_range_min ? parseFloat(formData.price_range_min) : null,
        price_range_max: formData.price_range_max ? parseFloat(formData.price_range_max) : null,
        portfolio_images: formData.portfolio_images
      };

      await axios.put(`${API}/professional/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Profile updated successfully!");
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        <Link to="/dashboard" className="flex items-center text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
            Profile Settings
          </h1>
          <p className="text-gray-600">Update your business profile, pricing, and portfolio</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Info */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg">Business Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                  placeholder="Your Business Name"
                  data-testid="profile-business-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bio">About Your Services</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  placeholder="Tell customers about your experience, skills, and what makes you stand out..."
                  className="min-h-[120px]"
                  data-testid="profile-bio"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="service_area">Service Area</Label>
                <Input
                  id="service_area"
                  value={formData.service_area}
                  onChange={(e) => setFormData({...formData, service_area: e.target.value})}
                  placeholder="e.g., Newark, NJ and surrounding areas"
                  data-testid="profile-service-area"
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Pricing (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                Set your pricing to help customers understand your rates. You can set an hourly rate, a price range, or both.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                  placeholder="e.g., 50"
                  data-testid="profile-hourly-rate"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price_range_min">Price Range Min ($)</Label>
                  <Input
                    id="price_range_min"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price_range_min}
                    onChange={(e) => setFormData({...formData, price_range_min: e.target.value})}
                    placeholder="e.g., 100"
                    data-testid="profile-price-min"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_range_max">Price Range Max ($)</Label>
                  <Input
                    id="price_range_max"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price_range_max}
                    onChange={(e) => setFormData({...formData, price_range_max: e.target.value})}
                    placeholder="e.g., 500"
                    data-testid="profile-price-max"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Portfolio Images */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="w-5 h-5 text-blue-600" />
                Portfolio Images
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                Upload up to 4 photos showcasing your work. These will be visible to customers on your profile.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.portfolio_images.map((img, index) => (
                  <div key={`portfolio-${index}-${img.slice(-15)}`} className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 group">
                    <img 
                      src={img} 
                      alt={`Portfolio ${index + 1}`} 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {formData.portfolio_images.length < 4 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-forest hover:text-forest transition-colors"
                    data-testid="add-portfolio-image"
                  >
                    <Camera className="w-8 h-8" />
                    <span className="text-sm">Add Photo</span>
                  </button>
                )}
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              
              <p className="text-xs text-gray-400">
                {formData.portfolio_images.length}/4 images uploaded. Max 5MB per image.
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              className="bg-forest hover:bg-forest-dark rounded-full px-8"
              disabled={saving}
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </main>

      <Footer />
    </div>
  );
}
